import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useBrand } from '../../brands/BrandProvider';
import { resolveBrand } from '../../brands/registry';

type Props = {
  onAccept: () => void;
  onReject: () => void;
  /** brandKey do evento/página pública (prioridade sobre o contexto). */
  brandKey?: string | null;
};

/**
 * Banner compacto de cookies (estilo card flutuante), com ícone e cores da marca.
 * Aceitar → libera Meta Pixel. Definições → opções de recusar marketing.
 */
const MetaConsentBanner: React.FC<Props> = ({ onAccept, onReject, brandKey }) => {
  const ctxBrand = useBrand();
  const brand = useMemo(
    () => (brandKey ? resolveBrand({ publicBrandKey: brandKey }) : ctxBrand),
    [brandKey, ctxBrand]
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const accent = brand.publicPage?.accent || brand.publicPage?.ctaStart || '#4B91F7';
  const ctaBg = brand.publicPage?.ctaStart || accent;
  const ctaEnd = brand.publicPage?.ctaEnd || ctaBg;
  const iconSrc =
    brand.key === 'zuadaria'
      ? '/brands/zuadaria-icon.png'
      : brand.logoUrl || '/meusacramento01.svg';

  return (
    <Box
      role="dialog"
      aria-label="Consentimento de cookies"
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 20 },
        right: { xs: 12, sm: 'auto' },
        bottom: { xs: 12, sm: 20 },
        zIndex: 1400,
        width: { xs: 'auto', sm: 380 },
        maxWidth: 'calc(100vw - 24px)',
      }}
    >
      <Box
        sx={{
          bgcolor: '#fff',
          color: '#1f2937',
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18)',
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.06)',
          p: 2,
          position: 'relative',
        }}
      >
        <IconButton
          size="small"
          aria-label="Fechar"
          onClick={onReject}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            color: 'text.secondary',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ pr: 3 }}>
          <Box
            component="img"
            src={iconSrc}
            alt={brand.displayName}
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              objectFit: 'contain',
              flexShrink: 0,
              bgcolor: brand.key === 'zuadaria' ? 'rgba(143, 58, 58, 0.08)' : 'rgba(75, 27, 122, 0.08)',
              p: 0.5,
            }}
          />
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.45, color: '#1f2937', fontSize: '0.95rem' }}
            >
              Utilizamos cookies para melhorar a sua experiência.
            </Typography>
            <Link
              href="/politica-de-privacidade"
              underline="always"
              sx={{
                mt: 0.5,
                display: 'inline-block',
                fontWeight: 700,
                fontSize: '0.875rem',
                color: accent,
              }}
            >
              Termos & Políticas
            </Link>
          </Box>
        </Stack>

        {settingsOpen ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Cookies de marketing (Meta Pixel) medem visitas e inscrições vindas de anúncios.
              Você pode aceitar ou recusar.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={onReject}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  borderColor: accent,
                  color: accent,
                  '&:hover': { borderColor: accent, bgcolor: `${accent}12` },
                }}
              >
                Recusar
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={onAccept}
                disableElevation
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: ctaBg,
                  backgroundImage: `linear-gradient(135deg, ${ctaBg} 0%, ${ctaEnd} 100%)`,
                  '&:hover': { bgcolor: ctaEnd },
                }}
              >
                Aceitar
              </Button>
            </Stack>
          </Box>
        ) : (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSettingsOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                borderColor: accent,
                color: accent,
                py: 1,
                '&:hover': { borderColor: accent, bgcolor: `${accent}12` },
              }}
            >
              Definições
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onAccept}
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1,
                bgcolor: ctaBg,
                backgroundImage: `linear-gradient(135deg, ${ctaBg} 0%, ${ctaEnd} 100%)`,
                color: '#fff',
                '&:hover': { bgcolor: ctaEnd },
              }}
            >
              Aceitar
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MetaConsentBanner;
